import { Localization } from '@aa-app-localization'
import { DimensionUnits, Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import {
    HighlightedProductFragment,
    ProductFragment,
    ProductImageFragment,
    ProductImageTypeEnum,
    SimpleProductFragment,
} from '@app-graphql/schema'
import { ProductHit } from '@app-types/product-hit.types'
import { Translated } from '@app-types/common.types'
import { curry } from 'ramda'

export type ProductTypes = HighlightedProductFragment | ProductFragment | SimpleProductFragment

export const translateProduct = curry(<T extends ProductTypes>(
    lang: Localization.Locale,
    productFragment: T,
): Translated<T> => {
    return {
        ...productFragment,
        ...productFragment.translations[lang],
    }
})

export function getProductImageOfType(
    images: ProductImageFragment[],
    type: ProductImageTypeEnum,
): ProductImageFragment {
    return images.find((i) => i.type === type) ?? images[0] ?? {
        type: ProductImageTypeEnum.Default,
        alt: '',
        url: '/assets/images/missing-image-icon.svg',
    }
}

export function getProductImagesOfType(
    images: ProductImageFragment[],
    type: ProductImageTypeEnum,
): ProductImageFragment[] {
    return images.filter((i) => i.type === type) ?? images
}

function buildImagesFromHit(hit: ProductHit): ProductImageFragment[] {
    const candidates: Array<{ image?: ProductHit['primaryImage']; type: ProductImageTypeEnum }> = [
        { image: hit.primaryImage, type: ProductImageTypeEnum.Front },
        { image: hit.ambianceImage, type: ProductImageTypeEnum.Group },
        { image: hit.wideImage, type: ProductImageTypeEnum.Default },
    ]
    const seen = new Set<string>()
    const images: ProductImageFragment[] = []

    for (const { image, type } of candidates) {
        if (! image?.url || seen.has(image.url)) {
            continue
        }

        seen.add(image.url)
        images.push({
            __typename: 'ProductImage',
            url: image.url,
            type,
            alt: image.alt ?? null,
        })
    }

    return images
}

export function transformHit(hit: ProductHit): Product {
    const dimensions = {
        diameter: hit.dimensions.diameter,
        height: hit.dimensions.height,
        length: hit.dimensions.length,
        units: DimensionUnits.cm,
        width: hit.dimensions.width,
    }

    return {
        ambianceImage: hit.ambianceImage?.url ?? '',
        brand: hit.brand,
        uniqueHash: '',
        categoryNames: hit.categories,
        codeLocal: hit.codeLocal,
        colors: (hit.colors ?? []),
        collectionName: hit.range,
        range: hit.range,
        debtorPrice: hit.suggestedRetailPriceNlInCents,
        deliveryTimeInWeeks: hit.deliveryTimeInWeeks,
        dimensions,
        description: hit.shortDescription || hit.description,
        ean: hit.ean,
        fsc: hit.isFsc ?? false,
        id: hit.ean,
        images: buildImagesFromHit(hit),
        inFavourites: false,
        inStock: true,
        materials: hit.materials ?? [],
        name: hit.title,
        newDebtorPrice: hit.suggestedStandardRetailPriceNlInCents,
        newProduct: hit.isNew,
        packages: [],  // Because it does not come from Algolia,
        primaryImage: hit.primaryImage?.url ?? '',
        productOrderSteps: hit.orderStep,
        slug: hit.slug,
        status: hit.status,
        statusEnum: hit.statusOriginal,
        subColors: (hit.subColors ?? []),
        subMaterials: (hit.subMaterials ?? []),
        suggestedRetailPrice: hit.suggestedRetailPriceNlInCents,
        suggestedRetailPriceISE: hit.suggestedRetailPriceIseInCents,
        suggestedStandardRetailPrice: hit.suggestedStandardRetailPriceNlInCents,
        suggestedStandardRetailPriceISE: hit.suggestedStandardRetailPriceIseInCents,
        totalPackages: '0',
        wideImage: hit.wideImage?.url ?? '',
    }
}

